/**
 * DateRangeSelector
 * Date range selection with preset buttons and custom date picker
 */

import { useState, useEffect } from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Calendar } from '@/core/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/core/components/ui/popover';
import { cn } from '@/core/lib/utils';
import { DateRangePreset, AnalyticsDateRange } from '../../types/dashboard-types';

interface DateRangeSelectorProps {
  selectedPreset: DateRangePreset;
  dateRange: AnalyticsDateRange;
  onPresetChange: (preset: DateRangePreset) => void;
  onDateRangeChange: (dateRange: AnalyticsDateRange) => void;
  isLoading?: boolean;
}

const PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: '7 Days' },
  { value: '30days', label: '30 Days' },
];

export function DateRangeSelector({
  selectedPreset,
  dateRange,
  onPresetChange,
  onDateRangeChange,
  isLoading = false,
}: DateRangeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<{ from?: Date; to?: Date } | undefined>(
    selectedPreset === 'custom'
      ? {
          from: new Date(dateRange.dateFrom),
          to: new Date(dateRange.dateTo),
        }
      : undefined
  );

  // Sync tempDateRange with dateRange when dateRange changes (only if custom selected)
  useEffect(() => {
    if (selectedPreset === 'custom') {
      setTempDateRange({
        from: new Date(dateRange.dateFrom),
        to: new Date(dateRange.dateTo),
      });
    } else {
      setTempDateRange(undefined);
    }
  }, [dateRange.dateFrom, dateRange.dateTo, selectedPreset]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format date as YYYY-MM-DD in local timezone (not UTC)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateSelect = (dates: { from?: Date; to?: Date } | undefined) => {
    setTempDateRange(dates);
  };

  const handleApply = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      onDateRangeChange({
        dateFrom: formatDateLocal(tempDateRange.from),
        dateTo: formatDateLocal(tempDateRange.to),
      });
      // Don't call onPresetChange('custom') - setDateRange already handles this
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setTempDateRange(
      selectedPreset === 'custom'
        ? {
            from: new Date(dateRange.dateFrom),
            to: new Date(dateRange.dateTo),
          }
        : undefined
    );
    setIsOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {PRESETS.map((preset) => (
          <Button
            key={preset.value}
            variant={selectedPreset === preset.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => onPresetChange(preset.value)}
            disabled={isLoading}
          >
            {preset.label}
          </Button>
        ))}

        {/* Custom date range picker */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={(selectedPreset === 'custom') ? 'default' : 'outline'}
              size="sm"
              disabled={isLoading}
              className={cn(
                'gap-2',
                (selectedPreset === 'custom') && 'bg-yellow-500 text-white hover:bg-yellow-600'
              )}
            >
              <CalendarIcon className="h-4 w-4" />
              {(selectedPreset === 'custom') ? (
                <span>
                  {formatDate(dateRange.dateFrom)} - {formatDate(dateRange.dateTo)}
                </span>
              ) : (
                <span>Custom</span>
              )}
            </Button>
          </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="range"
            selected={tempDateRange}
            onSelect={handleDateSelect}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
            defaultMonth={tempDateRange?.from || new Date()}
            classNames={{
              day_selected: "bg-yellow-500 text-white hover:bg-yellow-600 hover:text-white focus:bg-yellow-600 focus:text-white",
              day_range_middle: "aria-selected:bg-yellow-100 aria-selected:text-yellow-900",
              cell: "[&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected])]:bg-yellow-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            }}
          />
          <div className="flex justify-end gap-2 p-3 border-t">
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={!tempDateRange?.from || !tempDateRange?.to}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      </div>
    </div>
  );
}
